import API_CONFIG from '../apiConfig';

const API_URL = API_CONFIG.BASE_URL;
const SUBMISSIONS_URL = `${API_CONFIG.BASE_URL}/portal-user-all-submissions`;
const SUBMISSION_DETAILS_URL = `${API_CONFIG.BASE_URL}/submission-details`;

// ==================== Helper Functions ====================
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

const safeFileName = (file) => {
  if (!file) return 'Unknown File';
  if (file.title && typeof file.title === 'string' && file.title.trim() !== '') {
    return file.title.trim();
  }
  if (file.file && typeof file.file === 'string' && file.file.trim() !== '') {
    return file.file.split('/').pop();
  }
  return 'Unknown File';
};

const parseInvoiceStatus = (invoice) => {
  if (!invoice) return 'Pending';
  
  let statusVal = invoice.status;
  
  if (statusVal === undefined || statusVal === null) {
    statusVal = invoice['STATUS (EN)'] || invoice['status (en)'] || invoice['status(en)'] || invoice.status_en || invoice.status_EN;
  }
  
  if (!statusVal) return 'Pending';
  if (typeof statusVal === 'string') return statusVal;
  if (typeof statusVal === 'object') {
    return statusVal.en || 
           statusVal['en'] || 
           statusVal['STATUS (EN)'] || 
           statusVal['status (en)'] || 
           statusVal['status(en)'] || 
           statusVal.status_en || 
           statusVal.status || 
           statusVal.label || 
           Object.values(statusVal)[0] || 
           'Pending';
  }
  return String(statusVal);
};

// ==================== fetchSubmissions ====================
export const fetchSubmissions = async () => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const response = await fetch(`${SUBMISSIONS_URL}?_t=${Date.now()}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
      },
    });

    const text = await response.text();

    if (!text || text.trim() === '') {
      throw new Error(`Server returned empty response (HTTP ${response.status})`);
    }
    if (text.trimStart().startsWith('<!DOCTYPE') || text.trimStart().startsWith('<html')) {
      throw new Error(`Server error (HTTP ${response.status}). Please try again later.`);
    }

    let data;
    try { data = JSON.parse(text); } catch {
      throw new Error(`Unexpected server response (HTTP ${response.status})`);
    }

    if (!response.ok) {
      throw new Error(data?.message || data?.error || `Request failed with status ${response.status}`);
    }

    // ✅ ROOT FIX: The API returns the FULL submission list under data["0"].data
    // data.allSubmissions.data only contains a FILTERED SUBSET (submissions with purchases attached)
    // data["0"].data contains ALL submissions (including Published ones)
    const submissionsData = data['0']?.data || data.listings?.data || data.allSubmissions?.data || [];

    // 🔍 Debug: log the raw status values coming from the API
    const rawStatuses = [...new Set(submissionsData.map(s => s.status))];
    console.log('[fetchSubmissions] Raw status values from API:', rawStatuses);

    // Exact API status values (confirmed):
    // Pending | Published | Approved | Edit | Dropped | Deleted | UnderReview | Rejected | In Printing
    const statusMap = {
      // ── Pending ──────────────────────────────────
      'Pending':        ['all', 'pending'],
      // ── Under Review ─────────────────────────────
      'UnderReview':    ['all', 'inreview'],
      // ── Approved ─────────────────────────────────
      'Approved':       ['all', 'approved'],
      // ── Published ────────────────────────────────
      'Published':      ['all', 'published'],
      // ── In Printing ──────────────────────────────
      'In Printing':    ['all', 'printing'],
      // ── Edit (Revision requested) ─────────────────
      'Edit':           ['all', 'edit'],
      // ── Dropped → archived ───────────────────────
      'Dropped':        ['all', 'archived'],
      // ── Deleted → archived ───────────────────────
      'Deleted':        ['all', 'archived'],
      // ── Rejected ─────────────────────────────────
      'Rejected':       ['all', 'rejected'],
    };

    // Build an enrichment lookup from allSubmissions (has purchased_services / available_upgrades)
    // keyed by submission id — merge the LATEST payment entry per id
    const enrichmentMap = {};
    (data.allSubmissions?.data || []).forEach(item => {
      const key = item.id?.toString();
      if (key) {
        // If multiple records share same id (different payment_id), merge upgrades/services
        if (!enrichmentMap[key]) {
          enrichmentMap[key] = {
            purchased_services: item.purchased_services || '[]',
            available_upgrades: item.available_upgrades || '[]',
            checkPackageAvailable: item.checkPackageAvailable ?? item.check_package_available,
          };
        } else {
          // Merge purchased_services arrays across payment records
          try {
            const existing = JSON.parse(enrichmentMap[key].purchased_services || '[]');
            const incoming = JSON.parse(item.purchased_services || '[]');
            const merged = [...existing, ...incoming].filter(
              (s, i, arr) => arr.findIndex(x => x.id === s.id) === i
            );
            enrichmentMap[key].purchased_services = JSON.stringify(merged);
          } catch { /* keep existing */ }
        }
      }
    });

    const submissions = submissionsData.map((item) => {
      // Lookup: exact match → strip whitespace match → fallback 'pending'
      const rawStatus = (item.status || '').trim();
      const status = statusMap[rawStatus]
        || statusMap[rawStatus.replace(/\s+/g, '')]   // e.g. "In Printing" → "InPrinting" fallback
        || (() => {
             console.warn(`[fetchSubmissions] Unknown status: "${rawStatus}" — defaulting to pending`);
             return ['all', 'pending'];
           })();
      const keywords = item.keyword ? item.keyword.split(',').map((k) => k.trim()).filter(Boolean) : [];
      const authorsList = (item.coAuthors || []).map((a) => `${a.authorfName} ${a.authorlName}`);

      // Enrich with purchased_services / available_upgrades from allSubmissions lookup
      const enriched = enrichmentMap[item.id?.toString()] || {};

      return {
        id: item.id?.toString() || '0',
        status,
        title: item.title || 'No Title',
        date: formatDate(item.created_at),
        abstract: item.abstract || 'No abstract available',
        words: item.abstract ? item.abstract.split(' ').length : 0,
        pages: Math.ceil((item.abstract?.length || 0) / 500) || 1,
        authors: authorsList.length > 0 ? authorsList : ['No authors'],
        keywords: keywords.length > 0 ? keywords : ['No keywords'],
        language: item.paperLanguage || 'English',
        submittedDate: formatDate(item.created_at),
        paperType: item.paperType || 'Abstract',
        paperLink: item.paperLink || '',
        coAuthors: item.coAuthors || [],
        reviews: [],
        files: item.image ? [{ id: 1, name: item.image, type: 'final paper', date: formatDate(item.created_at), isAuto: true }] : [],
        invoices: [],
        purchased_services: enriched.purchased_services ?? item.purchased_services ?? '[]',
        available_upgrades: enriched.available_upgrades ?? item.available_upgrades ?? '[]',
        submissionType: item.submissionType || 'Conference',
        checkPackageAvailable: enriched.checkPackageAvailable ?? item.checkPackageAvailable ?? item.check_package_available,
      };
    });

    return { submissions };
  } catch (error) {
    console.error('fetchSubmissions:', error);
    throw error;
  }
};

// ==================== fetchSubmissionById ====================
export const fetchSubmissionById = async (id) => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    console.log('[fetchSubmissionById] Fetching:', id);

    const response = await fetch(`${SUBMISSION_DETAILS_URL}/${id}?_t=${Date.now()}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
      },
    });

    console.log('[fetchSubmissionById] Status:', response.status);

    const text = await response.text();

    if (response.status === 500) {
      console.warn('[fetchSubmissionById] Server 500, trying from list...');
      return await fetchSubmissionFromList(id);
    }

    if (!text || text.trim() === '') {
      throw new Error(`Server returned empty response (HTTP ${response.status})`);
    }
    if (text.trimStart().startsWith('<!DOCTYPE') || text.trimStart().startsWith('<html')) {
      throw new Error(`Server error (HTTP ${response.status}). Please try again later.`);
    }

    let data;
    try { data = JSON.parse(text); } catch {
      throw new Error(`Unexpected server response (HTTP ${response.status})`);
    }

    if (!response.ok) {
      throw new Error(data?.message || data?.error || `Request failed with status ${response.status}`);
    }

    const paperData = data?.paperDetails || data?.['0'] || {};
    const authorsList = (paperData?.coAuthors || [])
      .map((a) => `${a?.authorfName || ''} ${a?.authorlName || ''}`.trim())
      .filter(Boolean);

    const keywords = typeof paperData?.keywords === 'string'
      ? paperData.keywords.split(',').map((k) => k.trim()).filter(Boolean)
      : [];

    let allFiles = [];

    if (data?.files && data.files.length > 0) {
      allFiles = data.files.map((file) => ({
        id: file?.id || Math.random(),
        name: safeFileName(file),
        fileName: file?.file || '',
        type: file?.type || (file?.title === 'Full Paper' ? 'Full Paper' : 'Other'),
        date: formatDate(file?.created_at),
        visibility: file?.visibility || 'private',
        title: file?.title || '',
        file: file?.file || '',
        created_at: file?.created_at || null,
        updated_at: file?.updated_at || null,
        originalFile: file || {},
        isAuto: false,
      }));
    }

    if (paperData?.mainPaper && 
        paperData.mainPaper !== 'https://www.emiratesscholar.com/system/publish/' && 
        paperData.mainPaper !== 'https://citescholar.com/portal/public/publish/') {
      
      const mainFileName = paperData.mainPaper.split('/').pop();
      const alreadyExists = allFiles.some(f => 
        f.file === paperData.mainPaper || f.fileName === mainFileName
      );
      
      if (!alreadyExists) {
        allFiles.push({
          id: Date.now(),
          name: mainFileName || 'Main Paper',
          fileName: mainFileName || '',
          type: 'Full Paper',
          date: formatDate(paperData?.submitted_date),
          visibility: 'private',
          title: 'Full Paper',
          file: paperData.mainPaper,
          created_at: paperData?.submitted_date || null,
          updated_at: null,
          originalFile: { file: paperData.mainPaper },
          isAuto: true,
        });
      }
    }

    if (paperData?.finalPaper && 
        paperData.finalPaper !== paperData?.mainPaper &&
        paperData.finalPaper !== 'https://www.emiratesscholar.com/system/publish/' &&
        paperData.finalPaper !== 'https://citescholar.com/portal/public/publish/') {
      
      const finalFileName = paperData.finalPaper.split('/').pop();
      const alreadyExists = allFiles.some(f => 
        f.file === paperData.finalPaper || f.fileName === finalFileName
      );
      
      if (!alreadyExists) {
        allFiles.push({
          id: Date.now() + 1,
          name: finalFileName || 'Final Paper',
          fileName: finalFileName || '',
          type: 'Final Paper',
          date: formatDate(paperData?.expectedPublishDate || paperData?.submitted_date),
          visibility: 'private',
          title: 'Final Paper',
          file: paperData.finalPaper,
          created_at: null,
          updated_at: null,
          originalFile: { file: paperData.finalPaper },
          isAuto: true,
        });
      }
    }

    console.log('Total files:', allFiles.length);

    const submission = {
      id: id?.toString() || '0',
      title: paperData?.title || 'No Title',
      journal: paperData?.journalName || 'No Journal',
      abstract: paperData?.abstract || 'No abstract available',
      status: paperData?.paperStatus || 'Unknown',
      conferenceId: paperData?.conference_id || data?.conference_id || data?.paperDetails?.conference_id || '',
      conference_id: paperData?.conference_id || data?.conference_id || data?.paperDetails?.conference_id || '',
      authors: authorsList.length > 0 ? authorsList : ['No authors'],
      coAuthors: (paperData?.coAuthors || []).map((author) => ({
        id: author?.id || null,
        authorfName: author?.authorfName || '',
        authorlName: author?.authorlName || '',
        email: author?.email || '',
        country: author?.country || '',
        organization: author?.organization || '',
        webpage: author?.webpage || '',
        corresponder: author?.corresponder || 'no',
        corresponding: author?.corresponding || false,
        orderNo: author?.orderNo || null,
        created_at: author?.created_at || null,
        updated_at: author?.updated_at || null,
      })),
      paperType: paperData?.paperType || 'Full Paper',
      language: paperData?.paperLanguage || 'English',
      submissionType: paperData?.submissionType || 'Conference',
      date: formatDate(paperData?.submitted_date),
      submittedDate: formatDate(paperData?.submitted_date),
      expectedPublishDate: paperData?.expectedPublishDate ? formatDate(paperData.expectedPublishDate) : null,
      paperLink: paperData?.mainPaper || '',
      finalPaper: paperData?.finalPaper || null,
      reviewStatus: data?.reviewStatus || false,
      reviews: (data?.peerReviews || []).map((review) => ({
        ...review,
        id: review?.id || null,
        reviewId: review?.reviewId || review?.id || '--',
        score: review?.score || review?.status || '--',
        comments: review?.comments || '',
        reviewer: review?.reviewer || {},
        reviewfile: review?.reviewfile || review?.reviewFile || review?.review_file || review?.file || review?.pdf || null,
        created_at: review?.created_at || null,
      })),
      files: allFiles,
      invoices: (data?.relatedInvoices || []).map((invoice) => {
        const parsedStatus = parseInvoiceStatus(invoice);
        const rawAmt = (invoice?.amount !== undefined && invoice?.amount !== null) ? invoice.amount : invoice?.total;
        return {
          ...invoice,
          id: invoice?.id || null,
          invoiceNumber: invoice?.invoice_no || invoice?.invoice_number || invoice?.id || '--',
          invoice_no: invoice?.invoice_no || '',
          title: invoice?.title || invoice?.package_name || invoice?.title_en || invoice?.description || '',
          amount: rawAmt !== undefined && rawAmt !== null ? rawAmt : '--',
          total: invoice?.total || invoice?.amount || '--',
          status: parsedStatus || invoice?.status || 'Pending',
          date: formatDate(invoice?.date || invoice?.created_at || invoice?.invoice_date),
          created_at: invoice?.created_at || null,
          updated_at: invoice?.updated_at || null,
          currency: invoice?.currency || 'USD',
          description: invoice?.description || '',
          payment_method: invoice?.payment_method || '',
          payment_status: parseInvoiceStatus({ status: invoice?.payment_status }) || parsedStatus || 'pending',
        };
      }),
      relatedInvoices: data?.relatedInvoices || [],
      keywords: keywords.length > 0 ? keywords : ['No keywords'],
      words: paperData?.abstract ? paperData.abstract.split(' ').length : 0,
      pages: Math.ceil((paperData?.abstract?.length || 0) / 500) || 1,
      dropButton: [true, 'true', 1, '1'].includes(paperData?.dropButton),
      paperStatus: paperData?.paperStatus || 'Unknown',
      submitted_date: paperData?.submitted_date || null,
      packagesList: data?.packagesList || data?.packages || data?.mandatoryPackageList || [],
      optionalPackagesList: data?.optionalPackagesList || data?.optionalPackages || [],
      fastTrack: Number(paperData?.fastTrack ?? paperData?.fast_track ?? data?.fastTrack ?? data?.fast_track ?? 0),
      paperRefeneces: paperData?.paperRefeneces || paperData?.paperReferences || data?.paperRefeneces || data?.paperReferences || paperData?.paper_reference || '',
      purchased_services: paperData?.purchased_services || data?.purchased_services || '[]',
      available_upgrades: paperData?.available_upgrades || data?.available_upgrades || '[]',
      checkPackageAvailable: data?.checkPackageAvailable ?? paperData?.checkPackageAvailable ?? data?.check_package_available ?? paperData?.check_package_available ?? true,
      // Published-only metadata fields
      paperDoi: paperData?.paperDoi || null,
      pageStart: paperData?.pageStart || null,
      pageEnd: paperData?.pageEnd || null,
      paperVolume: paperData?.paperVolume || null,
      paperIssue: paperData?.paperIssue || null,
    };

    console.log('[fetchSubmissionById] Success');
    return submission;
  } catch (error) {
    console.error('fetchSubmissionById:', error);
    throw error;
  }
};

const fetchSubmissionFromList = async (id) => {
  try {
    const { submissions } = await fetchSubmissions();
    const found = submissions.find(s => s.id === id.toString());
    
    if (found) {
      console.log('[fetchSubmissionFromList] Found in list');

      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${SUBMISSION_DETAILS_URL}/${id}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        });

        if (response.ok) {
          const text = await response.text();
          const data = JSON.parse(text);
          const paperData = data?.paperDetails || data?.['0'] || {};

          const listFiles = [...(found.files || [])];
          
          if (paperData?.mainPaper && 
              paperData.mainPaper !== 'https://www.emiratesscholar.com/system/publish/' && 
              paperData.mainPaper !== 'https://citescholar.com/portal/public/publish/') {
            listFiles.push({
              id: Date.now(),
              name: paperData.mainPaper.split('/').pop() || 'Main Paper',
              fileName: paperData.mainPaper.split('/').pop() || '',
              type: 'Full Paper',
              date: formatDate(paperData?.submitted_date),
              file: paperData.mainPaper,
              isAuto: true,
            });
          }

          return {
            id: found.id,
            title: paperData?.title || found.title,
            journal: paperData?.journalName || '--',
            abstract: paperData?.abstract || found.abstract,
            status: paperData?.paperStatus || Array.isArray(found.status) ? found.status[1] : found.status,
            authors: found.authors,
            coAuthors: paperData?.coAuthors || found.coAuthors || [],
            paperType: paperData?.paperType || found.paperType,
            language: paperData?.paperLanguage || found.language,
            submissionType: paperData?.submissionType || 'Conference',
            date: found.date,
            submittedDate: found.submittedDate,
            expectedPublishDate: null,
            paperLink: paperData?.mainPaper || found.paperLink,
            finalPaper: paperData?.finalPaper || null,
            reviewStatus: false,
            reviews: [],
            files: listFiles,
            invoices: (data?.relatedInvoices || []).map((invoice) => ({
              id: invoice?.id || null,
              invoiceNumber: invoice?.invoice_no || invoice?.id || '--',
              amount: invoice?.amount || '--',
              status: parseInvoiceStatus(invoice),
              date: formatDate(invoice?.date || invoice?.created_at),
            })),
            keywords: found.keywords,
            words: found.words,
            pages: found.pages,
            dropButton: false,
            paperStatus: paperData?.paperStatus || found.status,
            submitted_date: null,
            packagesList: data?.packagesList || data?.packages || data?.mandatoryPackageList || [],
            optionalPackagesList: data?.optionalPackagesList || data?.optionalPackages || [],
            fastTrack: Number(paperData?.fastTrack ?? paperData?.fast_track ?? data?.fastTrack ?? data?.fast_track ?? 0),
            paperRefeneces: paperData?.paperRefeneces || paperData?.paperReferences || data?.paperRefeneces || data?.paperReferences || paperData?.paper_reference || '',
            purchased_services: paperData?.purchased_services || data?.purchased_services || '[]',
            available_upgrades: paperData?.available_upgrades || data?.available_upgrades || '[]',
            checkPackageAvailable: data?.checkPackageAvailable ?? paperData?.checkPackageAvailable ?? data?.check_package_available ?? paperData?.check_package_available ?? found.checkPackageAvailable,
          };
        }
      } catch (e) {
        console.log('Could not fetch details, using list data only');
      }

      return {
        id: found.id,
        title: found.title,
        journal: '--',
        abstract: found.abstract,
        status: Array.isArray(found.status) ? found.status[1] : found.status,
        authors: found.authors,
        coAuthors: found.coAuthors || [],
        paperType: found.paperType,
        language: found.language,
        submissionType: 'Conference',
        date: found.date,
        submittedDate: found.submittedDate,
        expectedPublishDate: null,
        paperLink: found.paperLink,
        finalPaper: null,
        reviewStatus: false,
        reviews: [],
        files: found.files || [],
        invoices: [],
        keywords: found.keywords,
        words: found.words,
        pages: found.pages,
        dropButton: false,
        paperStatus: Array.isArray(found.status) ? found.status[1] : found.status,
        submitted_date: null,
        packagesList: found.packagesList || [],
        optionalPackagesList: found.optionalPackagesList || [],
        fastTrack: Number(found?.fastTrack ?? found?.fast_track ?? 0),
        paperRefeneces: found?.paperRefeneces || found?.paperReferences || found?.paper_reference || '',
        purchased_services: found.purchased_services || '[]',
        available_upgrades: found.available_upgrades || '[]',
        checkPackageAvailable: found.checkPackageAvailable,
      };
    }
    throw new Error('Submission not found in list');
  } catch (e) {
    console.error('fetchSubmissionFromList:', e);
    throw new Error('Could not load submission details.');
  }
};

// ==================== addCoAuthor ====================
export const addCoAuthor = async (submissionId, authorData) => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const fName = authorData.firstName || authorData.authorfName || authorData.first_name || '';
    const lName = authorData.lastName || authorData.authorlName || authorData.last_name || '';
    const org = authorData.organization || authorData.affiliation || '';

    const formData = new FormData();
    formData.append('journal_submission_id', submissionId);
    formData.append('publish_id', submissionId);
    formData.append('submission_id', submissionId);
    formData.append('authorfName', fName);
    formData.append('first_name', fName);
    formData.append('authorlName', lName);
    formData.append('last_name', lName);
    formData.append('email', authorData.email || '');
    formData.append('country', authorData.country || '');
    formData.append('organization', org);
    formData.append('affiliation', org);

    const response = await fetch(`${API_URL}/add-coauthor-to-submission`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      body: formData,
    });

    const text = await response.text();
    let data;
    if (text?.trim().startsWith('{')) {
      try { data = JSON.parse(text); } catch { /* ignore */ }
    }

    if (!response.ok) throw new Error(data?.message || data?.error || 'Failed to add co-author');
    return { success: true, data: data || {} };
  } catch (error) {
    console.error('addCoAuthor:', error);
    throw error;
  }
};

// ==================== deleteCoAuthor ====================
export const deleteCoAuthor = async (authorId) => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const response = await fetch(`${API_URL}/delete-coauthor-to-submission/${authorId}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    });

    const text = await response.text();
    if (!text || text.trim() === '' || text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
      return { success: true };
    }

    let data;
    if (text.trim().startsWith('{') || text.trim().startsWith('[')) {
      try { data = JSON.parse(text); } catch { /* ignore */ }
    }

    if ([200, 204, 302].includes(response.status) || response.ok) {
      return { success: true, data: data || {} };
    }
    throw new Error(data?.message || `Failed to delete co-author (${response.status})`);
  } catch (error) {
    console.error('deleteCoAuthor:', error);
    throw error;
  }
};

// ==================== uploadFileToSubmission ====================
export const uploadFileToSubmission = async (publishId, file, title = 'Full Paper') => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const formData = new FormData();
    formData.append('publish_id', publishId);
    formData.append('title', title);
    formData.append('file', file);

    console.log('[uploadFileToSubmission] Uploading:', { publishId, title, fileName: file.name });

    const response = await fetch(`${API_URL}/upload-file-to-submission`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
      body: formData,
    });

    const text = await response.text();
    console.log('[uploadFileToSubmission] Status:', response.status);

    let data;
    if (text?.trim().startsWith('{')) {
      try { data = JSON.parse(text); } catch { /* ignore */ }
    }

    if (!response.ok) {
      throw new Error(data?.message || data?.error || 'Failed to upload file');
    }

    return { success: true, data: data || {} };
  } catch (error) {
    console.error('uploadFileToSubmission:', error);
    throw error;
  }
};

// ==================== deleteFileFromSubmission ====================
export const deleteFileFromSubmission = async (fileId) => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const response = await fetch(`${API_URL}/delete-paper-file/${fileId}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    });

    const text = await response.text();
    if (!text || text.trim() === '' || text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
      return { success: true };
    }

    let data;
    if (text.trim().startsWith('{') || text.trim().startsWith('[')) {
      try { data = JSON.parse(text); } catch { /* ignore */ }
    }

    if ([200, 204, 302].includes(response.status) || response.ok) {
      return { success: true, data: data || {} };
    }
    throw new Error(data?.message || data?.error || 'Failed to delete file');
  } catch (error) {
    console.error('deleteFileFromSubmission:', error);
    throw error;
  }
};

// ==================== fetchChairId ====================
export const fetchChairId = async (eventId) => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const response = await fetch(`${API_CONFIG.BASE_URL}/user-dashboard-page`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    });

    const text = await response.text();
    let data;
    try { data = JSON.parse(text); } catch { throw new Error('Invalid JSON response'); }

    if (!response.ok) throw new Error(data?.message || 'Failed to fetch user data');

    const chairId = eventId || data?.chair_id || data?.user?.chair_id || data?.event?.id || data?.event_id || data?.user?.event_id || 1;

    return { success: true, chairId, data };
  } catch (error) {
    console.error('fetchChairId:', error);
    return { success: false, chairId: eventId || 1, error: error.message };
  }
};

// ==================== createNewSubmission ====================
export const createNewSubmission = async (submissionData) => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    // ✅ جلب chairId من localStorage
    const stored = JSON.parse(localStorage.getItem('event_context') || '{}');
    const chairId = submissionData.chair_id || stored?.chairId || stored?.eventId || 1032;

    const rawTopics = submissionData.submissionTopics || submissionData.p_topics || [];
    const pTopics = Array.isArray(rawTopics)
      ? rawTopics
      : typeof rawTopics === 'string' && rawTopics.trim() !== ''
        ? rawTopics.split(',').map(t => t.trim()).filter(Boolean)
        : [];
    const corresponding = submissionData.authors?.[0] || {};
    const submissionAuthors = submissionData.authors?.slice(1).map(author => ({
      authorfName: author.first_name || '',
      authorlName: author.last_name || '',
      email: author.email || '',
      country: author.country || '',
      organization: author.affiliation || '',
      webpage: author.webpage || '',
      corresponder: 'no'
    })) || [];
    const addonQty = submissionData.mainPackageQuantities || {};

    // Find the first selected package (quantity > 0)
    let selectedPackageId = '';
    let selectedQty = 0;
    Object.entries(addonQty).forEach(([id, qty]) => {
      if (qty > 0 && !selectedPackageId) {
        selectedPackageId = id;
        selectedQty = qty || 1;
      }
    });

    console.log('[createNewSubmission] Data:', {
      chair_id: chairId,
      title: submissionData.submissionTitle,
      keywords: submissionData.keywords,
      topics: pTopics,
      corresponding: corresponding.email,
      coAuthors: submissionAuthors.length,
      packages: addonQty,
      package_id: selectedPackageId,
      qty: selectedQty || 1,
      amount: submissionData.grandTotal || 0,
      hasFile: !!submissionData.manuscript
    });

    let response;

    if (submissionData.manuscript) {
      const formData = new FormData();
      
      formData.append('chair_id', chairId);
      formData.append('title_paper', submissionData.submissionTitle);
      formData.append('abstract', submissionData.submissionAbstract);
      formData.append('keywords', submissionData.keywords || '');
      formData.append('language', submissionData.language || 'English');
      formData.append('paper_type', submissionData.paperType);
      formData.append('total', submissionData.grandTotal || 0);
      formData.append('amount', submissionData.grandTotal || 0);
      formData.append('total_amount', submissionData.grandTotal || 0);
      formData.append('package_id', selectedPackageId);
      formData.append('packageId', selectedPackageId);
      formData.append('qty', selectedQty || 1);
      formData.append('quantity', selectedQty || 1);
      formData.append('agree_terms', 'true');
      formData.append('agree_original', 'true');
      formData.append('references', submissionData.paper_reference || '');
      formData.append('reference', submissionData.paper_reference || '');
      formData.append('paper_reference', submissionData.paper_reference || '');
      
      pTopics.forEach((topic, index) => {
        formData.append(`p_topics[${index}]`, topic);
        formData.append(`p_topics[]`, topic);
        formData.append(`topics[${index}]`, topic);
        formData.append(`topics[]`, topic);
        formData.append(`submissionTopics[${index}]`, topic);
        formData.append(`submissionTopics[]`, topic);
      });
      
      formData.append('corresponding[first_name]', corresponding.first_name || '');
      formData.append('corresponding[last_name]', corresponding.last_name || '');
      formData.append('corresponding[email]', corresponding.email || '');
      formData.append('corresponding[country]', corresponding.country || '');
      formData.append('corresponding[organization]', corresponding.affiliation || '');
      
      submissionAuthors.forEach((author, index) => {
        formData.append(`submissionAuthors[${index}][authorfName]`, author.authorfName);
        formData.append(`submissionAuthors[${index}][authorlName]`, author.authorlName);
        formData.append(`submissionAuthors[${index}][email]`, author.email);
        formData.append(`submissionAuthors[${index}][country]`, author.country);
        formData.append(`submissionAuthors[${index}][organization]`, author.organization);
        formData.append(`submissionAuthors[${index}][webpage]`, author.webpage || '');
        formData.append(`submissionAuthors[${index}][corresponder]`, 'no');
      });
      
      Object.entries(addonQty).forEach(([key, value]) => {
        formData.append(`addonQty[${key}]`, value || 1);
      });

      if (submissionData.packageData) {
        submissionData.packageData.forEach((pkg, index) => {
          formData.append(`packageData[${index}][id]`, pkg.id);
          formData.append(`packageData[${index}][price]`, pkg.price);
          formData.append(`packageData[${index}][quantity]`, pkg.quantity || pkg.qty || 1);
          formData.append(`packageData[${index}][total]`, pkg.total);
        });
      }
      
      formData.append('manuscript', submissionData.manuscript);

      response = await fetch(`${API_URL}/new-submission`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
        body: formData,
      });
    } else {
      const body = {
        chair_id: chairId,
        title_paper: submissionData.submissionTitle,
        abstract: submissionData.submissionAbstract,
        keywords: submissionData.keywords || '',
        language: submissionData.language || 'English',
        paper_type: submissionData.paperType,
        p_topics: pTopics,
        submissionTopics: pTopics,
        corresponding: {
          first_name: corresponding.first_name || '',
          last_name: corresponding.last_name || '',
          email: corresponding.email || '',
          country: corresponding.country || '',
          organization: corresponding.affiliation || ''
        },
        submissionAuthors: submissionAuthors,
        addonQty: addonQty,
        packageData: (submissionData.packageData || []).map(pkg => ({
          ...pkg,
          quantity: pkg.quantity || pkg.qty || 1,
          qty: pkg.qty || pkg.quantity || 1
        })),
        total: submissionData.grandTotal || 0,
        amount: submissionData.grandTotal || 0,
        total_amount: submissionData.grandTotal || 0,
        package_id: selectedPackageId,
        packageId: selectedPackageId,
        qty: selectedQty || 1,
        quantity: selectedQty || 1,
        agree_terms: true,
        agree_original: true,
        references: submissionData.paper_reference || '',
        reference: submissionData.paper_reference || '',
        paper_reference: submissionData.paper_reference || ''
      };

      response = await fetch(`${API_URL}/new-submission`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });
    }

    const responseText = await response.text();
    let data = {};
    if (responseText && responseText.trim() !== '') {
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.warn('⚠️ [createNewSubmission] Response text is non-JSON:', responseText.slice(0, 200));
        if (!response.ok) {
          throw new Error(`Server error (${response.status}): ${responseText.slice(0, 150)}`);
        }
        data = { success: true, message: responseText };
      }
    }

    console.log('[createNewSubmission] Response:', data);

    if (!response.ok) {
      throw new Error(data?.message || data?.error || 'Submission failed');
    }

    return data;
  } catch (error) {
    console.error('createNewSubmission:', error);
    throw error;
  }
};

// ==================== fetchPaperTopics ====================
export const fetchPaperTopics = async (eventId = 1032) => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const response = await fetch(`${API_URL}/paper-topics-list?event_id=${eventId}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    });

    const text = await response.text();
    let data;
    try { data = JSON.parse(text); } catch { throw new Error('Invalid JSON response'); }

    if (!response.ok) throw new Error(data?.message || 'Failed to fetch topics');

    const topicsList = (data?.topicsList || data?.data || []).map((topic) => ({
      id: topic?.id || null,
      title: topic?.title || '',
      status: topic?.status || '',
      chair_id: topic?.chair_id || null,
    }));

    return { success: true, data: topicsList, topicsList };
  } catch (error) {
    console.error('fetchPaperTopics:', error);
    throw error;
  }
};

// ==================== fetchEsrcTopics ====================
export const fetchEsrcTopics = async () => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    console.log('📡 [fetchEsrcTopics] Fetching from esrc-topics-list...');
    const response = await fetch(`${API_URL}/esrc-topics-list`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    });

    const text = await response.text();
    let data;
    try { data = JSON.parse(text); } catch { throw new Error('Invalid JSON response'); }

    console.log('📡 [fetchEsrcTopics] Raw response data:', data);

    if (!response.ok) throw new Error(data?.message || 'Failed to fetch topics');

    let rawList = [];
    if (Array.isArray(data)) {
      rawList = data;
    } else if (data) {
      rawList = data.topicsList || data.data || data.topics || data.esrcTopicsList || data.esrc_topics || data.esrcTopics || (Array.isArray(data.listings) ? data.listings : []);
    }

    const topicsList = rawList.map((topic) => {
      if (typeof topic === 'string') {
        return {
          id: null,
          title: topic,
          status: 'active',
          chair_id: 1002
        };
      }
      return {
        id: topic?.id || null,
        title: topic?.title || topic?.name || topic?.topic_name || '',
        status: topic?.status || '',
        chair_id: topic?.chair_id || null,
      };
    });

    console.log('📡 [fetchEsrcTopics] Parsed topics count:', topicsList.length);

    return { success: true, data: topicsList, topicsList };
  } catch (error) {
    console.error('fetchEsrcTopics:', error);
    throw error;
  }
};

// ==================== fetchPackages ====================
export const fetchPackages = async (eventId = 1032) => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    console.log('[fetchPackages] Fetching packages for event:', eventId);

    const response = await fetch(`${API_URL}/submissions-packages-list-for-all?event_id=${eventId}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    });

    const text = await response.text();
    let data;
    try { data = JSON.parse(text); } catch { throw new Error('Invalid JSON response'); }

    if (!response.ok) throw new Error(data?.message || 'Failed to fetch packages');

    console.log('[fetchPackages] Loaded package data:', data);

    return { success: true, data };
  } catch (error) {
    console.error('fetchPackages:', error);
    throw error;
  }
};

// ==================== fetchEventDetails ====================
export const fetchEventDetails = async (eventToken) => {
  try {
    const token = localStorage.getItem('auth_token');
    const headers = { Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    console.log('[fetchEventDetails] Fetching:', eventToken);

    const response = await fetch(`${API_URL}/event-details?event=${eventToken}`, {
      method: 'GET',
      headers,
    });

    const text = await response.text();
    let data;
    try { data = JSON.parse(text); } catch { throw new Error('Invalid JSON response'); }

    if (!response.ok) throw new Error(data?.message || 'Failed to fetch event details');

    const result = {
      success: true,
      data: {
        eventId: data?.eventId || data?.id || null,
        eventName: data?.eventName || data?.name || '',
        logo: data?.logo || '',
        chairId: data?.chairId || data?.eventId || data?.id || null,
      },
    };

    console.log('[fetchEventDetails] Processed:', result);
    return result;
  } catch (error) {
    console.error('fetchEventDetails:', error);
    throw error;
  }
};

// ==================== fetchSubmissionPackages ====================
export const fetchSubmissionPackages = async () => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const response = await fetch('https://emiratesscholar.com/system/api/submission-details', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });

    const text = await response.text();
    let data;
    try { data = JSON.parse(text); } catch { throw new Error('Invalid JSON response'); }

    if (!response.ok) throw new Error(data?.message || 'Failed to fetch packages');
    return data;
  } catch (error) {
    console.error('fetchSubmissionPackages:', error);
    throw error;
  }
};

// ==================== generateInvoiceForPaperSubmission ====================
export const generateInvoiceForPaperSubmission = async (payload) => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    // Ensure packages always have at least quantity 1
    const processedPackages = (payload.packages || []).map(pkg => ({
      ...pkg,
      quantity: pkg.quantity || pkg.qty || 1,
      qty: pkg.qty || pkg.quantity || 1
    }));

    const finalPayload = {
      ...payload,
      packages: processedPackages,
      qty: payload.qty || (processedPackages.length > 0 ? processedPackages[0].qty : 1),
      quantity: payload.quantity || (processedPackages.length > 0 ? processedPackages[0].quantity : 1)
    };

    const response = await fetch('https://emiratesscholar.com/system/api/generate-new-invoice-for-paper-submission', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(finalPayload),
    });

    const text = await response.text();
    let data;
    try { data = JSON.parse(text); } catch { throw new Error('Invalid JSON response'); }

    if (!response.ok) throw new Error(data?.message || 'Failed to generate invoice');
    return data;
  } catch (error) {
    console.error('generateInvoiceForPaperSubmission:', error);
    throw error;
  }
};

// ==================== dropSubmission ====================
export const dropSubmission = async (id) => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const response = await fetch(`https://emiratesscholar.com/system/api/drop-my-submission/${id}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });

    const text = await response.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch {
      // Ignored
    }

    if (!response.ok) {
      throw new Error(data?.message || data?.error || 'Failed to drop submission');
    }
    return data;
  } catch (error) {
    console.error('dropSubmission:', error);
    throw error;
  }
};

// ==================== fetchFastTrackList ====================
export const fetchFastTrackList = async () => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) return [];

    const response = await fetch('https://emiratesscholar.com/system/api/get-fast-track', {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      return data?.getfastTrack?.data || [];
    }
  } catch (error) {
    console.error('fetchFastTrackList:', error);
  }
  return [];
};